import { useEffect, useMemo } from "react";
import {
  Avatar,
  Badge,
  Box,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import logo from "../../assets/img/new_ss_logo.png";
import { altAttribute } from "../../utils/utils";
import { CLIENT_ADMIN_NAV, filterClientAdminNav } from "../config/SideNav";
import { isExternalHref } from "../constants";

const NavLinkItem = ({ item, isActive, onNavigate, collapsed }) => {
  const itemClass = [
    "edsimsV2-sidenav-item",
    isActive ? "edsimsV2-sidenav-item--active" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const content = (
    <>
      <ListItemIcon className="edsimsV2-sidenav-item-icon-wrap" aria-hidden="true">
        <i className={`${item.icon} edsimsV2-sidenav-item-icon`} aria-hidden="true" />
      </ListItemIcon>
      <ListItemText
        disableTypography
        primary={<span className="edsimsV2-sidenav-item-label">{item.label}</span>}
      />
      {item.badge ? (
        <Badge badgeContent={item.badge} className="edsimsV2-sidenav-badge" />
      ) : null}
    </>
  );

  const external = isExternalHref(item.href, item.external);
  const openInNewTab = external || Boolean(item.openInNewTab);
  const button = openInNewTab ? (
    <ListItemButton
      component={external ? "a" : RouterLink}
      href={external ? item.href : undefined}
      to={external ? undefined : item.href}
      target="_blank"
      rel="noopener noreferrer"
      selected={isActive}
      className={itemClass}
      onClick={onNavigate}
      aria-label={`${item.label} (opens in a new tab)`}
      aria-current={isActive ? "page" : undefined}
    >
      {content}
    </ListItemButton>
  ) : (
    <ListItemButton
      component={RouterLink}
      to={item.href}
      selected={isActive}
      className={itemClass}
      onClick={onNavigate}
      aria-label={item.label}
      aria-current={isActive ? "page" : undefined}
    >
      {content}
    </ListItemButton>
  );

  if (!collapsed) return button;

  return (
    <Tooltip title={item.label} placement="right" arrow enterDelay={300}>
      {button}
    </Tooltip>
  );
};

const SectionLabel = ({ children }) => (
  <p className="edsimsV2-sidenav-section">{children}</p>
);

const SideNav = ({
  activeId,
  accType,
  roleName,
  userName,
  avatarUrl,
  userId,
  open = false,
  onClose,
  collapsed = false,
  onNavToggle,
}) => {
  const location = useLocation();

  const items = useMemo(
    () => filterClientAdminNav(CLIENT_ADMIN_NAV, { accType, roleName }),
    [accType, roleName]
  );

  const mainItems = items.filter((i) => i.group === "main");
  const resourceItems = items.filter((i) => i.group === "resources");
  const adminItems = items.filter((i) => i.group === "admin");

  const initials = (userName || "U")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();

  const resolveActive = (item) => {
    if (activeId) return item.id === activeId;
    if (isExternalHref(item.href, item.external) || item.openInNewTab) return false;
    return (
      location.pathname === item.href ||
      location.pathname.startsWith(`${item.href}/`)
    );
  };

  useEffect(() => {
    if (!open || !onClose) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  const displayName = userName || "User";
  const profileTo = userId ? `/users/profile/${userId}` : null;

  const sidenavClass = [
    "edsimsV2-sidenav",
    open ? "edsimsV2-sidenav--open" : "",
    collapsed ? "edsimsV2-sidenav--collapsed" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <Box
        className={`edsimsV2-sidenav-backdrop${open ? " edsimsV2-sidenav-backdrop--visible" : ""}`}
        onClick={onClose}
        aria-hidden={!open}
        inert={!open ? true : undefined}
        tabIndex={-1}
      />

      <Box
        component="nav"
        className={sidenavClass}
        aria-label="Main navigation"
      >
        <Box className="edsimsV2-sidenav-logo-wrap">
          <Box
            component="img"
            src={logo}
            alt={altAttribute.logoAlt}
            className="edsimsV2-sidenav-logo"
          />
          <IconButton
            type="button"
            className="edsimsV2-nav-toggle edsimsV2-nav-toggle--sidenav-mobile"
            aria-label="Close navigation"
            onClick={onNavToggle || onClose}
            size="small"
          >
            <i className="fas fa-bars" aria-hidden="true" />
          </IconButton>
        </Box>

        <Box className="edsimsV2-sidenav-scroll">
          <SectionLabel>Main</SectionLabel>
          <List dense disablePadding className="edsimsV2-sidenav-list">
            {mainItems.map((item) => (
              <NavLinkItem
                key={item.id}
                item={item}
                isActive={resolveActive(item)}
                onNavigate={onClose}
                collapsed={collapsed}
              />
            ))}
          </List>

          {resourceItems.length > 0 ? (
            <>
              <SectionLabel>Resources</SectionLabel>
              <List dense disablePadding className="edsimsV2-sidenav-list">
                {resourceItems.map((item) => (
                  <NavLinkItem
                    key={item.id}
                    item={item}
                    isActive={resolveActive(item)}
                    onNavigate={onClose}
                    collapsed={collapsed}
                  />
                ))}
              </List>
            </>
          ) : null}

          {adminItems.length > 0 ? (
            <>
              <SectionLabel>Admin</SectionLabel>
              <List dense disablePadding className="edsimsV2-sidenav-list">
                {adminItems.map((item) => (
                  <NavLinkItem
                    key={item.id}
                    item={item}
                    isActive={resolveActive(item)}
                    onNavigate={onClose}
                    collapsed={collapsed}
                  />
                ))}
              </List>
            </>
          ) : null}
        </Box>

        <Box className="edsimsV2-sidenav-footer">
          <Box className="edsimsV2-sidenav-user">
            <Avatar
              src={avatarUrl}
              alt=""
              className="edsimsV2-sidenav-avatar"
              variant="rounded"
            >
              {initials}
            </Avatar>
            <Box className="edsimsV2-sidenav-user-meta">
              {profileTo ? (
                <Box
                  component={RouterLink}
                  to={profileTo}
                  className="edsimsV2-sidenav-user-name"
                  title="Profile"
                  aria-label={`Open profile for ${displayName}`}
                >
                  {displayName}
                </Box>
              ) : (
                <p className="edsimsV2-sidenav-user-name">{displayName}</p>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default SideNav;
